import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, CreditCard, Link2, Loader2, Plus } from 'lucide-react';
import { PageHeader, EmptyState } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Brand } from '@/components/shared/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox, Switch, Textarea } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageLoader } from '@/components/ui/loader';
import { ApiRequestError, apiGet, apiPatch, apiPost } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useT } from '@/lib/i18n';

type PaymentLink = {
  id: string;
  token: string;
  title: string;
  description: string | null;
  amount: string | null;
  currency: string;
  vatIncluded: boolean;
  maxPayments: number | null;
  paidCount: number;
  paidTotal: string;
  isActive: boolean;
  expiresAt: string | null;
  createdAt: string;
  url: string;
};

const failed = (error: unknown, fallback: string) => toast.error(error instanceof ApiRequestError ? error.message : fallback);
const aed = (value: string | null, currency = 'AED') => (value === null ? null : `${currency} ${Number(value).toLocaleString('en-AE', { minimumFractionDigits: 2 })}`);

/** Commerce → Payment links: take a card payment without raising an invoice. */
export function PaymentLinksPage() {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: links = [], isLoading } = useQuery({ queryKey: ['payment-links'], queryFn: () => apiGet<PaymentLink[]>('/commerce/payment-links') });
  const [creating, setCreating] = useState(false);
  const toggle = useMutation({
    mutationFn: (link: PaymentLink) => apiPatch(`/commerce/payment-links/${link.id}`, { isActive: !link.isActive }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['payment-links'] }),
    onError: (error) => failed(error, 'Could not update the link'),
  });
  const copy = (url: string) => {
    void navigator.clipboard.writeText(url);
    toast.success(t('Link copied'));
  };
  return (
    <div>
      <PageHeader
        title="Payment links"
        description="Share a link, get paid by card. For deposits, bookings, one-off services — no invoice needed. Paid through your Telr, N-Genius or Stripe account."
        actions={
          <Can permission="finance.paymentlink.create">
            <Button onClick={() => setCreating(true)}>
              <Plus /> {t('Create payment link')}
            </Button>
          </Can>
        }
      />
      {isLoading ? (
        <PageLoader />
      ) : links.length === 0 ? (
        <EmptyState icon={<Link2 className="h-6 w-6" />} title="No payment links yet" description="Create one for a deposit, a consultation fee or a product, and send it on WhatsApp." />
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-[12px] font-bold text-foreground/80">
              <tr>
                <th className="px-4 py-2.5 text-start">{t('Link')}</th>
                <th className="px-4 py-2.5 text-start">{t('Amount')}</th>
                <th className="hidden px-4 py-2.5 text-start md:table-cell">{t('Payments')}</th>
                <th className="hidden px-4 py-2.5 text-start md:table-cell">{t('Collected')}</th>
                <th className="px-4 py-2.5 text-start">{t('Status')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={link.id} className="border-t">
                  <td className="px-4 py-3">
                    <p className="font-semibold">{link.title}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(link.createdAt)}</p>
                  </td>
                  <td className="tabular px-4 py-3">{aed(link.amount, link.currency) ?? <span className="text-muted-foreground">{t('Customer chooses')}</span>}</td>
                  <td className="tabular hidden px-4 py-3 md:table-cell">
                    {link.paidCount}
                    {link.maxPayments ? ` / ${link.maxPayments}` : ''}
                  </td>
                  <td className="tabular hidden px-4 py-3 font-semibold md:table-cell">{aed(link.paidTotal, link.currency)}</td>
                  <td className="px-4 py-3">
                    <Can permission="finance.paymentlink.update" fallback={<Badge variant={link.isActive ? 'success' : 'secondary'}>{link.isActive ? t('Active') : t('Off')}</Badge>}>
                      <Switch checked={link.isActive} onCheckedChange={() => toggle.mutate(link)} />
                    </Can>
                  </td>
                  <td className="px-4 py-3 text-end">
                    <Button size="sm" variant="outline" onClick={() => copy(link.url)}>
                      <Copy /> {t('Copy')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <CreateLinkDialog onClose={() => setCreating(false)} onCreated={(link) => copy(link.url)} />}
    </div>
  );
}

function CreateLinkDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (link: PaymentLink) => void }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ title: '', description: '', amount: '', open: false, vatIncluded: true, maxPayments: '', expiresAt: '' });
  const create = useMutation({
    mutationFn: () =>
      apiPost<PaymentLink>('/commerce/payment-links', {
        title: form.title,
        description: form.description || undefined,
        amount: form.open ? undefined : form.amount,
        vatIncluded: form.vatIncluded,
        maxPayments: form.maxPayments ? Number(form.maxPayments) : undefined,
        expiresAt: form.expiresAt || undefined,
      }),
    onSuccess: (link) => {
      void queryClient.invalidateQueries({ queryKey: ['payment-links'] });
      onCreated(link);
      onClose();
    },
    onError: (error) => failed(error, 'Could not create the link'),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Create payment link')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('What is it for?')}</Label>
            <Input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder={t('e.g. Booking deposit — Marina viewing')} autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label>{t('Description (optional)')}</Label>
            <Textarea rows={2} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.open} onCheckedChange={(checked) => setForm({ ...form, open: checked === true })} />
            {t('Let the customer enter the amount')}
          </label>
          {!form.open && (
            <div className="space-y-1.5">
              <Label>{t('Amount (AED)')}</Label>
              <Input type="number" min="1" step="0.01" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.vatIncluded} onCheckedChange={(checked) => setForm({ ...form, vatIncluded: checked === true })} />
            {t('Price includes 5% VAT')}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('Stop after (payments)')}</Label>
              <Input type="number" min="1" value={form.maxPayments} onChange={(event) => setForm({ ...form, maxPayments: event.target.value })} placeholder={t('Unlimited')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('Expires on')}</Label>
              <Input type="date" value={form.expiresAt} onChange={(event) => setForm({ ...form, expiresAt: event.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => create.mutate()} disabled={!form.title.trim() || (!form.open && !form.amount) || create.isPending}>
            {t('Create and copy link')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** /pl/:token — the customer's side. */
export function PublicPaymentLinkPage() {
  const { token = '' } = useParams();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['public-link', token],
    queryFn: () =>
      apiGet<{ title: string; description: string | null; amount: string | null; currency: string; vatIncluded: boolean; business: string; payable: boolean; provider: string | null }>(`/public/payment-links/${token}`),
    retry: false,
  });
  const [form, setForm] = useState({ name: '', email: '', amount: '' });
  const pay = useMutation({
    mutationFn: () => apiPost<{ url: string }>(`/public/payment-links/${token}/pay`, { name: form.name, email: form.email, amount: data?.amount ? undefined : form.amount }),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (caught) => failed(caught, 'Could not open the payment page'),
  });
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#fff6f2] px-4 py-10" style={{ colorScheme: 'light' }}>
      <div className="w-full max-w-md rounded-2xl border bg-white p-7 text-slate-900 shadow-brand-lg">
        {isLoading ? (
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        ) : isError || !data ? (
          <p className="text-center text-slate-600">{error instanceof ApiRequestError ? error.message : 'This payment link is not active.'}</p>
        ) : (
          <>
            <p className="text-sm font-semibold text-slate-500">{data.business}</p>
            <h1 className="mt-1 text-2xl font-bold">{data.title}</h1>
            {data.description && <p className="mt-2 whitespace-pre-wrap text-slate-600">{data.description}</p>}
            {data.amount && (
              <p className="mt-5 text-4xl font-bold tracking-tight">
                {data.currency} {Number(data.amount).toLocaleString('en-AE', { minimumFractionDigits: 2 })}
                {data.vatIncluded && <span className="ms-2 align-middle text-sm font-medium text-slate-500">incl. VAT</span>}
              </p>
            )}
            {!data.payable ? (
              <p className="mt-6 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">This business does not take card payments online yet.</p>
            ) : (
              <form
                className="mt-6 space-y-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  pay.mutate();
                }}
              >
                {!data.amount && (
                  <input required type="number" min="1" step="0.01" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} placeholder={`Amount (${data.currency})`} className="h-11 w-full rounded-lg border border-slate-200 px-3 outline-none focus:border-primary" />
                )}
                <input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Your name" className="h-11 w-full rounded-lg border border-slate-200 px-3 outline-none focus:border-primary" />
                <input required type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="Email for the receipt" className="h-11 w-full rounded-lg border border-slate-200 px-3 outline-none focus:border-primary" />
                <Button type="submit" className="h-11 w-full" disabled={pay.isPending}>
                  {pay.isPending ? <Loader2 className="animate-spin" /> : <CreditCard />} Pay by card
                </Button>
                {data.provider && <p className="text-center text-xs text-slate-500">Secure payment by {data.provider}</p>}
              </form>
            )}
          </>
        )}
        <div className="mt-8 flex items-center justify-center gap-1.5 text-xs text-slate-400">
          Powered by <Brand size="sm" className="scale-75" />
        </div>
      </div>
    </div>
  );
}
